import { Box } from '@mui/material';
import { dispatchUnwrap } from 'app/utils/store';
import { ChatBoxContext, ChatSessionContext } from 'modules/chat/providers';
import React, { FC, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Loader } from 'shared/components/Loader';
import Talk from 'talkjs';
import { updateUserConversation } from 'modules/users/store/users/repository';

interface ChatBoxProps {
  conversationId?: string | null | Talk.ConversationBuilder;
}

export const ChatBox: FC<ChatBoxProps> = ({ conversationId }) => {
  const [isMounted, setIsMounted] = useState(false);
  const { isLoaded, recipient } = useContext(ChatBoxContext);
  const [hasMessages, setHasMessages] = useState<boolean>(recipient?.hasConversations ?? false);
  const chatBoxEl = useRef(null);
  const { talkLoaded, session } = useContext(ChatSessionContext);

  const chatBox = useMemo(() => (session ? session.createChatbox() : null), [session]);

  useEffect(() => {
    if (chatBox && chatBoxEl.current) {
      chatBox.mount(chatBoxEl?.current).then(() => setIsMounted(true));
    }
  }, [chatBox]);

  useEffect(() => {
    if (chatBox && recipient && !hasMessages) {
      chatBox.onSendMessage(() => {
        dispatchUnwrap(updateUserConversation({ userId: recipient.id })).then(() => {
          setHasMessages(true);
        });
      });
    }
  }, [chatBox, hasMessages, recipient]);

  useEffect(() => {
    if (talkLoaded && session && conversationId && isLoaded && chatBox) {
      chatBox.select(conversationId);
    }
  }, [isLoaded, session, talkLoaded, conversationId, chatBox]);

  return (
    <Box width="100%" height="100%">
      {!isMounted && <Loader />}
      <Box
        sx={{ opacity: isMounted ? 1 : 0, visibility: isMounted ? 'visible' : 'hidden' }}
        width="100%"
        height="100%"
        ref={chatBoxEl}
      >
        <Loader />
      </Box>
    </Box>
  );
};
